import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import toast from 'react-hot-toast';
import { customerPortalService, default as customerPortalApi } from '../services/customerPortalApi';
import { useNavigate } from 'react-router-dom';
import {
  registerServiceWorker,
  getOrCreatePushSubscription,
  getPushSupportStatus,
  setVapidPublicKey,
  hasPushSubscriptions,
  runExclusivePushOperation,
  PUSH_ENABLE_TOAST_ID,
  PUSH_LOADING_MAX_MS
} from '../utils/pushService';

const arrayBufferToBase64 = (buffer) => {
  if (!buffer) return null;
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const CustomerNotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  preferences: null,
  refresh: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {},
  openNotificationPanel: () => {},
  ensurePushSubscription: async () => ({ success: false }),
  syncPushSubscriptionIfGranted: async () => ({ success: false }),
  pushSupport: { supported: false, reason: 'unknown' },
  hasServerPushSubscription: false
});

export const CustomerNotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [pushSupport, setPushSupport] = useState({ supported: false, reason: 'unknown' });
  const [vapidReady, setVapidReady] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const initialLoadRef = useRef(true);
  const lastSeenIdRef = useRef(null);
  const pollingRef = useRef(null);
  const serviceWorkerReadyRef = useRef(false);
  const metaLoadedRef = useRef(false);
  const notificationFetchInProgressRef = useRef(false);

  // localStorage'dan son görülen bildirim ID'sini yükle
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('customer-portal-last-seen-notification-id');
        if (stored) {
          lastSeenIdRef.current = parseInt(stored, 10);
        }
      }
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, []);

  const calculateUnread = useCallback((items = []) => {
    return items.filter((item) => !item.isRead).length;
  }, []);

  const fetchNotifications = useCallback(async (options = {}) => {
    const includeMeta = options?.includeMeta === true;
    const silent = options?.silent === true;
    if (!customerPortalService.isAuthenticated()) return;

    if (notificationFetchInProgressRef.current) {
      return;
    }

    notificationFetchInProgressRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      
      let notificationRes = { data: { success: true, data: { items: [] } } };
      const shouldLoadMeta = includeMeta || !metaLoadedRef.current;
      let prefRes = null;
      let vapidRes = null;
      
      try {
        notificationRes = await customerPortalService.get('/notifications?limit=20');
      } catch (err) {
        if (err?.response?.status !== 401) {
          // 401 dışındaki hatalar sessizce atlanıyor
        }
      }
      
      if (shouldLoadMeta) {
        try {
          prefRes = await customerPortalService.get('/notifications/preferences');
        } catch (err) {
          if (err?.response?.status !== 401) {
            // 401 dışındaki hatalar sessizce atlanıyor
          }
        }

        try {
          vapidRes = await customerPortalService.get('/notifications/vapid-key');
        } catch (err) {
          // VAPID key hatası önemli değil
        }
      }
      
      const items = notificationRes.data?.data?.items || [];
      setNotifications(items);
      setUnreadCount(calculateUnread(items));
      if (shouldLoadMeta) {
        setPreferences(prefRes?.data?.data || null);
        const key = vapidRes?.data?.data?.publicKey || vapidRes?.data?.publicKey || process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '';
        setVapidPublicKey(key);
        const supportStatus = getPushSupportStatus();
        setPushSupport(supportStatus);
        setVapidReady(!!key);
        metaLoadedRef.current = true;
      }
      handleIncomingNotifications(items);
    } catch (error) {
      // Sessizce atlanıyor
    } finally {
      notificationFetchInProgressRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [calculateUnread]);

  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        await customerPortalApi.put(`/notifications/${notificationId}/read`);
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notificationId ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
          )
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (error) {
        toast.error('Bildirim güncellenemedi', { duration: 3000 });
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await customerPortalApi.put('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success('Tüm bildirimler okundu işaretlendi', { duration: 3000 });
    } catch (error) {
      toast.error('Bildirimler güncellenemedi', { duration: 3000 });
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await customerPortalApi.delete(`/notifications/${notificationId}`);
      setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      toast.error('Bildirim silinemedi', { duration: 3000 });
    }
  }, []);

  const openNotificationPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const showToastForNotification = useCallback((notification) => {
    if (!notification || !notification.title) return;
    
    // Önceki toast'ları temizle (takılmayı önlemek için)
    toast.dismiss();
    
    try {
      const toastId = toast.custom(
        (t) => {
          if (!t || !t.id) return null;
          
          // Otomatik kapanma için timeout
          const autoCloseTimeout = setTimeout(() => {
            try {
              toast.dismiss(t.id);
            } catch (error) {
              // Sessizce atlanıyor
            }
          }, 4000); // 4 saniye sonra otomatik kapan
          
          const handleClose = (e) => {
            try {
              e?.stopPropagation();
              clearTimeout(autoCloseTimeout);
              toast.dismiss(t.id);
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          const handleClick = () => {
            try {
              clearTimeout(autoCloseTimeout);
              toast.dismiss(t.id);
              if (notification.metadata?.redirectUrl) {
                navigate(notification.metadata.redirectUrl);
              } else {
                openNotificationPanel();
              }
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          return (
            <div className="pointer-events-auto max-w-sm w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3 relative">
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition z-10"
                aria-label="Kapat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div
                onClick={handleClick}
                className="cursor-pointer pr-6"
              >
                <p className="text-xs text-gray-400 dark:text-gray-500">Yeni bildirim</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{notification.title || ''}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line">
                  {notification.body || ''}
                </p>
              </div>
            </div>
          );
        },
        { 
          duration: 4000,
          id: `notification-${notification.id || Date.now()}` // Unique ID ile çift toast önle
        }
      );
      
      // Güvenlik için maksimum 5 saniye sonra zorla kapat
      setTimeout(() => {
        try {
          toast.dismiss(toastId);
        } catch (error) {
          // Sessizce atlanıyor
        }
      }, 5000);
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, [navigate, openNotificationPanel]);

  const handleIncomingNotifications = useCallback(
    (items) => {
      if (!items.length) return;
      const newestId = Math.max(...items.map((item) => item.id));
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        lastSeenIdRef.current = newestId;
        if (typeof window !== 'undefined') {
          localStorage.setItem('customer-portal-last-seen-notification-id', newestId.toString());
        }
        return;
      }
      const lastSeen = lastSeenIdRef.current || 0;
      const newOnes = items.filter((item) => item.id > lastSeen);
      if (newOnes.length) {
        lastSeenIdRef.current = newestId;
        if (typeof window !== 'undefined') {
          localStorage.setItem('customer-portal-last-seen-notification-id', newestId.toString());
        }
        newOnes.reverse().forEach(showToastForNotification);
      }
    },
    [showToastForNotification]
  );

  const ensurePushSubscription = useCallback(async (options = {}) => {
    const silent = options.silent === true;

    if (silent) {
      if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
        return { success: false, skipped: true };
      }
    }

    return runExclusivePushOperation(async () => {
      const showError = (message) => {
        if (silent) return;
        toast.error(message, { id: PUSH_ENABLE_TOAST_ID, duration: 4000 });
      };
      const showSuccess = (message) => {
        if (silent) return;
        toast.success(message, { id: PUSH_ENABLE_TOAST_ID, duration: 3000 });
      };

      try {
        if (!silent) {
          toast.loading('Push bildirimleri etkinleştiriliyor...', {
            id: PUSH_ENABLE_TOAST_ID,
            duration: PUSH_LOADING_MAX_MS
          });
        }

        let registration;
        try {
          registration = await registerServiceWorker();
          serviceWorkerReadyRef.current = true;
        } catch (swError) {
          showError('Service Worker kaydedilemedi.');
          return { success: false, error: swError };
        }

        if (!registration?.pushManager) {
          showError('Push Manager bu tarayıcıda desteklenmiyor.');
          return { success: false };
        }

        if (
          !silent &&
          typeof window !== 'undefined' &&
          'Notification' in window &&
          Notification.permission === 'default'
        ) {
          toast.loading('Bildirim izni isteniyor...', {
            id: PUSH_ENABLE_TOAST_ID,
            duration: 4000
          });
        }

        let subscription;
        try {
          subscription = await getOrCreatePushSubscription(registration);
        } catch (subError) {
          showError(subError?.message || 'Push aboneliği oluşturulamadı');
          return { success: false, error: subError };
        }

        if (!subscription?.endpoint) {
          showError('Push aboneliği oluşturulamadı.');
          return { success: false };
        }

        const p256dhKey = subscription.getKey('p256dh');
        const authKey = subscription.getKey('auth');
        if (!p256dhKey || !authKey) {
          showError('Subscription keys bulunamadı.');
          return { success: false };
        }

        const p256dhBase64 = arrayBufferToBase64(p256dhKey);
        const authBase64 = arrayBufferToBase64(authKey);
        const userAgent = navigator.userAgent;
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        const browser = userAgent.includes('Edg')
          ? 'edge'
          : userAgent.includes('Chrome')
            ? 'chrome'
            : userAgent.includes('Firefox')
              ? 'firefox'
              : userAgent.includes('Safari')
                ? 'safari'
                : 'unknown';

        const subscriptionData = {
          endpoint: subscription.endpoint,
          keys: { p256dh: p256dhBase64, auth: authBase64 },
          metadata: {
            userAgent,
            browser,
            deviceType: isMobile ? 'mobile' : 'desktop',
            timestamp: new Date().toISOString()
          }
        };

        let subscribeResponse;
        try {
          subscribeResponse = await customerPortalService.post('/notifications/push-subscribe', {
            subscription: subscriptionData
          });
          if (!subscribeResponse?.data?.success) {
            throw new Error(subscribeResponse?.data?.message || 'Push subscription kaydedilemedi');
          }
        } catch (apiError) {
          showError(
            `Push aboneliği kaydedilemedi: ${apiError?.response?.data?.message || apiError?.message || 'Bilinmeyen hata'}`
          );
          return { success: false, error: apiError };
        }

        try {
          if (subscribeResponse?.data?.data) {
            setPreferences(subscribeResponse.data.data);
          } else {
            const prefRes = await customerPortalService.get('/notifications/preferences');
            if (prefRes?.data?.data) setPreferences(prefRes.data.data);
          }
        } catch (prefError) {
          // kritik değil
        }

        showSuccess(`Push bildirimleri ${isMobile ? 'mobil cihazda' : 'web tarayıcıda'} etkinleştirildi`);
        fetchNotifications({ includeMeta: true, silent: true });
        return { success: true };
      } catch (error) {
        showError(error?.message || 'Push bildirimi etkinleştirilemedi');
        return { success: false, error };
      }
    });
  }, [fetchNotifications, vapidReady]);

  const syncPushSubscriptionIfGranted = useCallback(async () => {
    return ensurePushSubscription({ silent: true });
  }, [ensurePushSubscription]);

  useEffect(() => {
    if (!customerPortalService.isAuthenticated()) {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      metaLoadedRef.current = false;
      initialLoadRef.current = true;
      lastSeenIdRef.current = null;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    
    const delay = initialLoadRef.current ? 200 : 100;
    const timeoutId = setTimeout(() => {
      if (!customerPortalService.isAuthenticated()) return;
      
      fetchNotifications({ includeMeta: true });
      initialLoadRef.current = false;
      
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(() => {
        if (customerPortalService.isAuthenticated()) {
          fetchNotifications({ silent: true });
        }
      }, 30000);
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [fetchNotifications]);

  useEffect(() => {
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible' && customerPortalService.isAuthenticated()) {
        fetchNotifications({ silent: true });
        syncPushSubscriptionIfGranted();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => document.removeEventListener('visibilitychange', visibilityHandler);
  }, [fetchNotifications, syncPushSubscriptionIfGranted]);

  useEffect(() => {
    if (!vapidReady || !customerPortalService.isAuthenticated()) return;
    const timer = setTimeout(() => {
      syncPushSubscriptionIfGranted();
    }, 400);
    return () => clearTimeout(timer);
  }, [vapidReady, syncPushSubscriptionIfGranted]);

  const hasServerPushSubscription = hasPushSubscriptions(preferences?.pushSubscription);

  const value = {
    notifications,
    unreadCount,
    loading,
    preferences,
    refresh: () => fetchNotifications({ includeMeta: true }),
    markAsRead,
    markAllAsRead,
    deleteNotification,
    openNotificationPanel,
    ensurePushSubscription,
    syncPushSubscriptionIfGranted,
    pushSupport,
    hasServerPushSubscription,
    isPanelOpen,
    setIsPanelOpen
  };

  return (
    <CustomerNotificationContext.Provider value={value}>
      {children}
    </CustomerNotificationContext.Provider>
  );
};

export const useCustomerNotifications = () => {
  const context = useContext(CustomerNotificationContext);
  if (!context) {
    throw new Error('useCustomerNotifications must be used within CustomerNotificationProvider');
  }
  return context;
};
